/**
 * EditorSettingsMenu — the gear icon in the editor top header (app-shell.tsx).
 * On mobile this is the SINGLE entry point for every editor action that used
 * to live in the tools row below the tabs (find, table of contents, writing
 * goal, AI assistant, version history, chapter notes) plus the writing
 * preferences (theme, font size, line spacing, writing effect, chapter
 * number). Sections are ordered by importance: actions first (search & the
 * table of contents are the most used), appearance/text settings after.
 *
 * Action items that don't have a prop here (find/goal/AI/versions/notes)
 * dispatch window events that `editor-view.tsx` listens for, since that view
 * owns the relevant open/closed state and is a sibling component.
 *
 * Self-contained: owns its own open state, outside-click/Escape dismissal,
 * and the enter/exit animation (motion/react, ~0.16s ease-out).
 */
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { Settings, Minus, Plus, Target, History, StickyNote, Share2, Download } from 'lucide-react';
import { GiArchiveResearch, GiPapers, GiGhost } from './icons/game-icons';
import { cn } from '../lib/utils';
import { ThemeSwitch } from './theme-switch';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import type { EditorSettings } from '../types';

interface EditorSettingsMenuProps {
  settings: EditorSettings;
  updateSettings: (patch: Partial<EditorSettings>) => void;
  /** Optional shortcut — opens the chapter table of contents (mobile TOC drawer). */
  onOpenTableOfContents?: () => void;
}

const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 26;

export function EditorSettingsMenu({ settings, updateSettings, onOpenTableOfContents }: EditorSettingsMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the menu, then dispatch a window event for an action owned by
  // editor-view.tsx (a sibling component — this menu has no direct access
  // to its state, so it broadcasts the request instead).
  const dispatchAction = (eventName: string) => {
    window.dispatchEvent(new Event(eventName));
    setOpen(false);
  };

  // Close on outside click and Escape
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const SPACINGS: { value: EditorSettings['lineHeight']; label: string }[] = [
    { value: 'compact', label: t('textSettings.spacingCompact', 'Compacto') },
    { value: 'normal', label: t('textSettings.spacingNormal', 'Normal') },
    { value: 'relaxed', label: t('textSettings.spacingRelaxed', 'Amplo') },
  ];

  const WRITING_EFFECTS: { value: EditorSettings['writingEffect']; label: string }[] = [
    { value: 'petals', label: t('textSettings.effectPetals', 'Pétalas') },
    { value: 'stardust', label: t('textSettings.effectStardust', 'Poeira Estelar') },
    { value: 'inkrain', label: t('textSettings.effectInkRain', 'Chuva de Tinta') },
    { value: 'sparks', label: t('textSettings.effectSparks', 'Faíscas de Choque') },
    { value: 'glitch', label: t('textSettings.effectGlitch', 'Faísca Glitch') },
    { value: 'mist', label: t('textSettings.effectMist', 'Névoa Cósmica') },
    { value: 'fire', label: t('textSettings.effectFire', 'Lareira em Chamas') },
    { value: 'fireflies', label: t('textSettings.effectFireflies', 'Vagalumes') },
    { value: 'neon', label: t('textSettings.effectNeon', 'Neon Elétrico') },
    { value: 'glyphs', label: t('textSettings.effectGlyphs', 'Eco Literário') },
    { value: 'none', label: t('textSettings.effectNone', 'Nenhum') },
  ];

  // Actions, ordered by importance — search and the table of contents are
  // the most used, so they open the menu.
  const ACTIONS: { key: string; label: string; Icon: ComponentType<{ size?: number; className?: string }>; onClick: () => void }[] = [
    {
      // Capítulos é a AÇÃO mais importante — primeira do menu.
      key: 'toc',
      label: t('editorView.chapters', 'Capítulos'),
      Icon: GiPapers,
      onClick: () => {
        if (onOpenTableOfContents) {
          onOpenTableOfContents();
          setOpen(false);
        } else {
          dispatchAction('toggle-toc');
        }
      },
    },
    {
      key: 'find',
      label: t('editorView.find', 'Localizar'),
      Icon: GiArchiveResearch,
      onClick: () => dispatchAction('editor-open-find'),
    },
    {
      key: 'goal',
      label: t('editorView.writingGoal', 'Meta de escrita'),
      Icon: Target,
      onClick: () => dispatchAction('editor-open-goal'),
    },
    {
      key: 'ai',
      label: t('editorView.aiAssistant', 'Assistente de IA'),
      Icon: GiGhost,
      onClick: () => dispatchAction('editor-toggle-aichat'),
    },
    {
      key: 'versions',
      label: t('editorView.versionHistory', 'Histórico de versões'),
      Icon: History,
      onClick: () => dispatchAction('editor-open-versions'),
    },
    {
      key: 'notes',
      label: t('editorView.chapterNotesMenu', 'Notas do capítulo'),
      Icon: StickyNote,
      onClick: () => dispatchAction('editor-open-notes'),
    },
    {
      key: 'share',
      label: t('editor.share', 'Compartilhar'),
      Icon: Share2,
      onClick: () => dispatchAction('editor-open-share'),
    },
    {
      key: 'publish',
      label: t('editor.publish', 'Publicar'),
      Icon: Download,
      onClick: () => dispatchAction('editor-publish'),
    },
  ];

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted/80',
          open && 'bg-muted text-foreground'
        )}
        title={t('editorSettingsMenu.title', 'Configurações do editor')}
        aria-label={t('editorSettingsMenu.title', 'Configurações do editor')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Settings size={18} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.94, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: -6 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            className="absolute right-0 top-[calc(100%+6px)] z-50 w-72 origin-top-right space-y-4 rounded-xl border border-border bg-popover p-3.5 shadow-lg ring-1 ring-foreground/10"
          >
            {/* Actions — ordered by importance. Search and the table of
                contents are the most used, so they come first. */}
            <div className="space-y-0.5">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t('editorSettingsMenu.sectionActions', 'Ações')}
              </label>
              <div className="flex flex-col">
                {ACTIONS.map(({ key, label, Icon, onClick }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={onClick}
                    className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-2 text-sm text-foreground transition-colors hover:bg-muted hover:text-primary"
                  >
                    <Icon size={16} className="text-muted-foreground" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Appearance & text — theme, font size, spacing, effects */}
            <div className="space-y-3 border-t border-border pt-3.5">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t('editorSettingsMenu.sectionAppearance', 'Aparência e texto')}
              </label>

              {/* App theme — light/dark, the "paper vs. night" reading mode */}
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-foreground">{t('settings.theme', 'Tema')}</span>
                <ThemeSwitch sz={16} />
              </div>
            </div>

            {/* Font size */}
            <div className="space-y-1.5">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t('textSettings.fontSizeLabel', 'Tamanho —')} <span className="text-foreground">{settings.fontSize}px</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => updateSettings({ fontSize: Math.max(MIN_FONT_SIZE, settings.fontSize - 1) })}
                  disabled={settings.fontSize <= MIN_FONT_SIZE}
                  className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                  aria-label={t('textSettings.decreaseFontSize', 'Diminuir fonte')}
                >
                  <Minus size={14} />
                </button>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${((settings.fontSize - MIN_FONT_SIZE) / (MAX_FONT_SIZE - MIN_FONT_SIZE)) * 100}%` }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => updateSettings({ fontSize: Math.min(MAX_FONT_SIZE, settings.fontSize + 1) })}
                  disabled={settings.fontSize >= MAX_FONT_SIZE}
                  className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                  aria-label={t('textSettings.increaseFontSize', 'Aumentar fonte')}
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Line height / spacing */}
            <div className="space-y-1.5">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t('textSettings.spacing', 'Espaçamento')}
              </label>
              <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/60 p-1">
                {SPACINGS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => updateSettings({ lineHeight: s.value })}
                    className={cn(
                      'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                      settings.lineHeight === s.value
                        ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Writing effect — particles emitted at the caret while typing */}
            <div className="space-y-1.5">
              <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t('textSettings.writingEffectLabel', 'Efeito ao escrever')}
              </label>
              <Select
                value={settings.writingEffect}
                onValueChange={(v) => updateSettings({ writingEffect: v as EditorSettings['writingEffect'] })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WRITING_EFFECTS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Toggle: show chapter number */}
            <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
              <span className="text-sm text-foreground">{t('textSettings.chapterNumber', 'Número do capítulo')}</span>
              <Switch
                checked={settings.showChapterNumber}
                onCheckedChange={(v) => updateSettings({ showChapterNumber: v })}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
